

import { Component, OnInit, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CourierService } from '../../../shared/services/courier.service';
import { GetFuelListResponse, GetFuelListRequest, PostMainFuelRequest, GetActiveVoucherDto, GetCarAndLoaderResponse, GetCarAndLoaderModel } from '../../../shared/models/courier.models';
import { ActiveUserLocalDataService } from '../../../shared/services/active-user-local-data.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  templateUrl: 'fuel-purchase.component.html',
  styleUrls: ['./fuel-purchase.component.scss']
})
export class FuelPurchaseComponent implements OnInit, OnChanges {
  private lastLoaderState: boolean = false;
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedCarAndLoader']) {
      const isLoader = this.isLoaderSelected;
      if (!isLoader && this.lastLoaderState) {
        this.engineHours = 0;
      }
      this.lastLoaderState = isLoader;
    }
  }

  fuelResponse: GetFuelListResponse | null = null;
  fuelAmount: number = 0;
  fuelReceiptNumber: number = 0;
  engineHours: number = 0;
  lastEngineHours: number | null = null;
  todayEngineHours: number | null = null;
  consumedEngineHours: number | null = null; // Difference between current input and lastEngineHours
  popupVisibleError: boolean = false;
  errorMessage: string = '';
  loading: boolean = true;
  isPopupVisible = false;
  isMobile: boolean = false;
  showPopup: boolean = false; // For engine hours validation popup
  popupMessage: string = '';
  voucher: GetActiveVoucherDto | null = null;
  carAndLoader: GetCarAndLoaderModel[] = [];
  selectedCarAndLoader: number | null = null;

  constructor(
    private courierService: CourierService,
    private activeUserLocalDataService: ActiveUserLocalDataService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
  this.checkIfMobile();
  this.voucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;

  if (this.voucher) {
    this.courierService.getCarAndLoader({ carId: this.voucher.carsId }).subscribe((response: GetCarAndLoaderResponse) => {

      const carText = this.translate.instant('Car');
      const loaderText = this.translate.instant('Loader');

      this.carAndLoader = response.carAndLoader.map(item => {
        let label = '';
        if (item.carType === 0) label = carText;
        else if (item.carType === 1) label = loaderText;
        return {
          ...item,
          carNumber: label ? `${item.carNumber} (${label})` : item.carNumber
        };
      });

      const car = this.carAndLoader.find(x => x.carType === 0);
      this.selectedCarAndLoader = car ? car.id : (this.carAndLoader[0]?.id ?? null);

      if (this.selectedCarAndLoader) {
        const initialSelected = this.carAndLoader.find(x => x.id === this.selectedCarAndLoader);
        if (initialSelected && initialSelected.carType === 1) {
          this.fetchLoaderMaxEngineHours(initialSelected.id, initialSelected.carType);
        }
      }

      this.checkLoadingComplete();
    }, error => {
      console.error('Error fetching Car and Loader data:', error);
      this.checkLoadingComplete();
    });
  } else {
    this.checkLoadingComplete();
  }

  this.loadFuelList();
}

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }

  loadFuelList() {
    this.loading = true;
    let activeVoucher = this.voucher;
  
    if (!activeVoucher) {
      activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;
    }
  
    if (activeVoucher) {
      const request: GetFuelListRequest = { voucherId: activeVoucher.id };
      this.courierService.getFuelList(request).subscribe((response: GetFuelListResponse) => {
        this.fuelResponse = response;
        // Debug: show received fuel list models
        if (response?.fuelList) {
          console.log('GetFuelListModel', response.fuelList);
        } else {
          console.log('GetFuelListModel: no fuelList returned in response');
        }
        this.computeLastEngineHours();
        this.computeTodayEngineHours();
        this.checkLoadingComplete();
      }, error => {
        console.error('Error fetching fuel list:', error);
        this.checkLoadingComplete();
      });
    } else {
      console.error('Active voucher is null');
      this.navigateToVoucher();
      this.checkLoadingComplete();
    }
  }

  private checkLoadingComplete() {
    if (this.carAndLoader.length > 0 && this.fuelResponse) {
      this.loading = false;
    }
  }

  navigateToVoucher() {
    const serviceVoucher = this.activeUserLocalDataService.activeServiceVoucher;
    const courierVoucher = this.activeUserLocalDataService.getActiveCourierVoucher();

    if (serviceVoucher) {
      this.router.navigate(['/service-voucher']);
    } else if (courierVoucher) {
      this.router.navigate(['/voucher']);
    } else {
      this.router.navigate(['/voucher']);
    }
  }

  saveClick() {
    const errors: string[] = [];
  
    if (!this.fuelAmount || this.fuelAmount <= 0) {
      errors.push(this.translate.instant('InvalidFuelAmountPleaseEnterAValidFuelAmount'));
    }
    if (this.isLoaderSelected) {
      if (!this.engineHours || this.engineHours < 0) {
        errors.push(this.translate.instant('InvalidEngineHoursPleaseEnterValidEngineHours'));
      }
    }
  
  if (this.fuelReceiptNumber == null || this.fuelReceiptNumber < 0) {
      errors.push(this.translate.instant('InvalidReceiptNumberPleaseEnterAValidReceiptNumber'));
    }
  
    if (errors.length > 0) {
      this.errorMessage = errors.join('<br/><br/>');
      this.popupVisibleError = true;
      return;
    }
  
    const activeVoucher = this.activeUserLocalDataService.getActiveCourierVoucher() || this.activeUserLocalDataService.activeServiceVoucher;
    if (!activeVoucher) {
      this.navigateToVoucher();
      return;
    }
  
    // Additional validation: engine hours must be greater than lastEngineHours when loader selected and lastEngineHours available
    if (this.isLoaderSelected && this.lastEngineHours != null && this.engineHours <= this.lastEngineHours) {
      this.popupMessage = this.translate.instant('PleaseEnterAValidEngineHoursValue');
      this.showPopup = true;
      return;
    }

    const request: PostMainFuelRequest = {
      receptNumber: this.fuelReceiptNumber.toString(),
      vouchersId: activeVoucher.id,
      fuelAmount: this.fuelAmount
    , engineHours: this.engineHours
    };
  
    if (this.carAndLoader && this.carAndLoader.length > 1 && this.selectedCarAndLoader !== null) {
      const selectedCar = this.carAndLoader.find(car => car.id === this.selectedCarAndLoader);
      if (selectedCar && selectedCar.id !== activeVoucher.carsId) {
        request.loaderId = selectedCar.id;
      }
    }

    this.courierService.postMainFuel(request).subscribe(() => {
      this.navigateToVoucher();
    });
  }

  closePopup() {
    this.showPopup = false;
  }
  onFuelAmountChanged(e: { value: any }) {
    let value = e.value;
    if (typeof value === 'string') {
      value = value.replace(',', '.');
      const parsed = parseFloat(value);
      this.fuelAmount = isNaN(parsed) ? 0 : parsed;
    } else {
      this.fuelAmount = value;
    }
  }
  get isLoaderSelected(): boolean {
    const selected = this.carAndLoader?.find((x: GetCarAndLoaderModel) => x.id === this.selectedCarAndLoader);
    return !!selected && selected.carType === 1;
  }
  ngDoCheck(): void {
    const isLoader = this.isLoaderSelected;
    if (isLoader !== this.lastLoaderState) {
      this.engineHours = 0;
      this.fuelAmount = 0;
  this.fuelReceiptNumber = 0;
      this.consumedEngineHours = null;
    }
    this.lastLoaderState = isLoader;
  }

  onEngineHoursChanged(e: { value?: any }) {
    const value = e && 'value' in e ? (e as any).value : undefined;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      this.engineHours = isNaN(parsed) ? 0 : parsed;
    } else {
      this.engineHours = value ?? 0;
    }
    this.computeConsumedEngineHours();
  }

  private computeLastEngineHours(): void {
    if (!this.fuelResponse || !this.fuelResponse.fuelList || this.fuelResponse.fuelList.length === 0) {
      this.lastEngineHours = null;
      this.computeConsumedEngineHours();
      return;
    }
    const withEngineHours = this.fuelResponse.fuelList
      .filter(x => x.engineHours != null && x.engineHours !== 0 && x.id != null);
    if (withEngineHours.length === 0) {
      this.lastEngineHours = null;
      this.computeConsumedEngineHours();
      return;
    }
    const latest = withEngineHours.reduce((prev, curr) => (curr.id! > (prev.id!)) ? curr : prev);
    this.lastEngineHours = latest.engineHours ?? null;
    this.computeConsumedEngineHours();
  }

  private computeTodayEngineHours(): void {
    if (!this.fuelResponse || !Array.isArray(this.fuelResponse.fuelList)) {
      this.todayEngineHours = 0; // show 0 when no data
      return;
    }
    const today = new Date();
    const total = this.fuelResponse.fuelList
      .filter(item => {
        if (item.engineHours == null || item.engineHours === 0) return false;
        if (!item.timeAdded) return false;
        const date = new Date(item.timeAdded);
        return date.getFullYear() === today.getFullYear() &&
               date.getMonth() === today.getMonth() &&
               date.getDate() === today.getDate();
      })
      .reduce((sum, item) => sum + (item.engineHours || 0), 0);
    this.todayEngineHours = total; // even if 0, display 0
    console.log('TodayEngineHours calculated:', this.todayEngineHours);
  }

  private computeConsumedEngineHours(): void {
    if (this.lastEngineHours != null && this.engineHours != null) {
      // Always show the difference (can be negative or zero) so user sees live feedback from first digit.
      this.consumedEngineHours = this.engineHours - this.lastEngineHours;
    } else {
      this.consumedEngineHours = null;
    }
  }

  onSelectedVehicleChanged(e: any) {
    const newValue = e?.value;
    this.selectedCarAndLoader = newValue;
    const selected = this.carAndLoader?.find(x => x.id === this.selectedCarAndLoader);
    if (selected) {
      if (selected.carType === 1) {
        this.fetchLoaderMaxEngineHours(selected.id, selected.carType);
      } else {
        this.computeLastEngineHours();
      }
      this.engineHours = 0;
      this.consumedEngineHours = null;
    }
  }

  private fetchLoaderMaxEngineHours(loaderId: number, carType: number) {
    try {
      this.courierService.getMaxEngineHours({ loaderId }).subscribe(
        (resp: any) => {
          const model = resp?.EngineHours ?? resp?.engineHours ?? null;
          const returned = model?.EngineHours ?? model?.engineHours ?? model?.maxEngineHours ?? null;
          console.log('Loader max engine hours fetch', { id: loaderId, carType, returned });
          this.lastEngineHours = returned == null ? 0 : returned;
          this.computeConsumedEngineHours();
        },
        (error: any) => {
          console.error('Error fetching max engine hours', error);
        }
      );
    } catch (err) {
      console.error('Unexpected error calling getMaxEngineHours', err);
    }
  }
}